import { DOCUMENT } from '@angular/common';
import {
    DestroyRef,
    Injectable,
    InjectionToken,
    computed,
    inject,
    signal,
    type Provider,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MonitoringService } from '@quipex/core/logging';
import { SELECTED_DOCUMENT_EXPORT_HANDOFF } from '@quipex/feature/building-context';
import {
    EMPTY,
    combineLatest,
    distinctUntilChanged,
    map,
    mergeMap,
    of,
} from 'rxjs';
import type { ActivityKey } from '../activity-journal';
import {
    activityIdentitiesEqual,
    hasInProgressActivity,
} from '../activity-journal';
import { projectActivityOutcomes } from '../activity-presentation';
import {
    activityActionExecutionKey,
    activityActionSnapshotTargetMatches,
    type ActivityActionResult,
    type ActivityActionSnapshot,
} from './action-contract';
import {
    activityActionFailureCategory,
    ActivityActionExecutor,
    isUnavailableActivityActionError,
} from './actions';
import { ActivityBuildingResolver } from './building-resolver';
import type {
    ActivityContext,
    ActivityToastPresentation,
} from './context-contract';
export type {
    ActivityContext,
    ActivityToastPresentation,
} from './context-contract';
import {
    activityBuildingIds,
    enrichmentRequestsEqual,
} from './context-helpers';
import { ActivityExportDownloader } from './export-downloader';
import { ActivityState } from './state';
import { waitForActivityBuildingResolution } from './terminal-toast-resolution';
import { ActivityToastOwner } from './toast-owner';

@Injectable()
class ActivityFacade implements ActivityContext {
    private readonly actionExecutor = inject(ActivityActionExecutor);
    private readonly buildingResolver = inject(ActivityBuildingResolver);
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    private readonly monitoring = inject(MonitoringService);
    private readonly state = inject(ActivityState);
    private readonly clock = signal(Date.now());
    private readonly actionsInFlight = new Set<string>();
    private readonly buildingResolution$ = combineLatest([
        toObservable(this.buildingResolver.settledBuildingPublicIds),
        toObservable(this.buildingResolver.identity),
        toObservable(this.buildingResolver.activeIdentityVersion),
    ]);
    private readonly outcomeProjection = computed(() =>
        projectActivityOutcomes(
            this.state.sections(),
            this.clock(),
            this.buildingResolver.lookups()
        )
    );
    private readonly enrichmentRequest = computed(() => ({
        identity: this.state.activeIdentity(),
        activeIdentityVersion: this.state.activeIdentityVersion(),
        buildingPublicIds: activityBuildingIds(this.state.items()),
    }));
    private readonly deniedBuildingPublicIdsToApply = computed(() => {
        if (
            !activityIdentitiesEqual(
                this.state.activeIdentity(),
                this.buildingResolver.identity()
            ) ||
            this.state.activeIdentityVersion() !==
                this.buildingResolver.activeIdentityVersion()
        ) {
            return [];
        }

        const deniedBuildingPublicIds =
            this.buildingResolver.deniedBuildingPublicIds();
        return [
            ...new Set(
                this.state
                    .items()
                    .filter((item) => item.availability !== 'unavailable')
                    .flatMap((item) =>
                        item.jobs.flatMap((job) =>
                            job.buildingPublicId &&
                            deniedBuildingPublicIds.has(job.buildingPublicId)
                                ? [job.buildingPublicId]
                                : []
                        )
                    )
            ),
        ];
    });

    public readonly activeIdentity = this.state.activeIdentity;
    public readonly activeIdentityVersion = this.state.activeIdentityVersion;
    public readonly groups = computed(() => this.outcomeProjection().groups);
    public readonly attentionCount = this.state.attentionCount;
    public readonly hasInProgress = computed(() =>
        hasInProgressActivity(this.state.items())
    );
    public readonly updatesDelayed = computed(
        () => this.state.connectionStatus().updatesDelayed
    );
    public readonly toastDecisions$ = this.state.toastDecisions$.pipe(
        mergeMap((decision) =>
            waitForActivityBuildingResolution(
                decision,
                this.buildingResolution$
            )
        ),
        mergeMap((decision) => {
            const buildingPublicId = decision.buildingPublicId;
            if (
                buildingPublicId &&
                activityIdentitiesEqual(
                    this.state.activeIdentity(),
                    decision.identity
                ) &&
                this.state.activeIdentityVersion() ===
                    decision.activeIdentityVersion &&
                this.buildingResolver
                    .deniedBuildingPublicIds()
                    .has(buildingPublicId)
            ) {
                this.state.markBuildingUnavailable(buildingPublicId);
                return EMPTY;
            }

            return of(decision);
        }),
        map((decision): ActivityToastPresentation => {
            const action =
                decision.action &&
                activityActionSnapshotTargetMatches(
                    this.state.actionFor(decision.key),
                    decision.action
                )
                    ? decision.action
                    : null;
            const outcome = this.outcomeProjection().toastFor({
                key: decision.key,
                kind: decision.kind,
                buildingPublicId: decision.buildingPublicId,
                statusMessage: decision.description,
                intent: action?.intent ?? null,
            });
            return {
                ...outcome,
                action:
                    action && outcome.action
                        ? {
                              type: outcome.action.type,
                              label: outcome.action.label,
                              execute: () =>
                                  this.runActionSnapshot(decision.key, action),
                          }
                        : null,
            };
        })
    );

    public constructor() {
        toObservable(this.deniedBuildingPublicIdsToApply)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((buildingPublicIds) => {
                for (const buildingPublicId of buildingPublicIds) {
                    this.state.markBuildingUnavailable(buildingPublicId);
                }
            });

        toObservable(this.enrichmentRequest)
            .pipe(
                distinctUntilChanged(enrichmentRequestsEqual),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((request) => this.buildingResolver.resolve(request));

        const interval = this.document.defaultView?.setInterval(
            () => this.clock.set(Date.now()),
            60_000
        );
        this.destroyRef.onDestroy(() => {
            if (interval !== undefined)
                this.document.defaultView?.clearInterval(interval);
        });
    }

    public markAttentionSeen(): void {
        this.clock.set(Date.now());
        this.state.markAttentionSeen(null);
    }

    public dismiss(key: ActivityKey): void {
        this.state.dismiss(key);
    }

    public async runAction(key: ActivityKey): Promise<ActivityActionResult> {
        return this.executeAction(key, () => this.state.actionFor(key));
    }

    private async runActionSnapshot(
        key: ActivityKey,
        action: ActivityActionSnapshot
    ): Promise<ActivityActionResult> {
        if (
            !activityIdentitiesEqual(
                this.state.activeIdentity(),
                action.identity
            ) ||
            this.state.activeIdentityVersion() !== action.activeIdentityVersion
        ) {
            return 'ignored';
        }

        return this.executeAction(key, () => action);
    }

    private async executeAction(
        key: ActivityKey,
        resolveAction: () => ActivityActionSnapshot | null
    ): Promise<ActivityActionResult> {
        const action = resolveAction();
        if (!action) return 'ignored';
        const executionKey = activityActionExecutionKey(key, action);
        if (this.actionsInFlight.has(executionKey)) return 'in-flight';

        this.actionsInFlight.add(executionKey);
        try {
            const execution = await this.actionExecutor.execute(action.intent);
            this.state.markActedOn(key, action);
            return execution;
        } catch (error) {
            this.monitoring.logEvent('ActivityActionFailed', {
                intentType: action.intent.type,
                category: activityActionFailureCategory(error),
            });
            if (isUnavailableActivityActionError(error)) {
                this.state.markUnavailable(key, action);
                return 'unavailable';
            }
            return 'failed';
        } finally {
            this.actionsInFlight.delete(executionKey);
        }
    }
}

export const ACTIVITY_CONTEXT = new InjectionToken<ActivityContext>(
    'ACTIVITY_CONTEXT'
);

export function provideActivity(): readonly Provider[] {
    return [
        ActivityActionExecutor,
        ActivityBuildingResolver,
        ActivityExportDownloader,
        ActivityState,
        {
            provide: SELECTED_DOCUMENT_EXPORT_HANDOFF,
            useExisting: ActivityState,
        },
        ActivityToastOwner,
        ActivityFacade,
        { provide: ACTIVITY_CONTEXT, useExisting: ActivityFacade },
    ];
}

export function injectActivity(): ActivityContext {
    return inject(ACTIVITY_CONTEXT);
}
